import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { JobApplicationForm } from '../components/JobApplicationForm';
import { getAllJobs, getUniqueDepartments, type Job } from '../data/jobs';
import { ArrowRight, MapPin, Clock, ArrowUpRight, Filter } from 'lucide-react';

export function Careers() {
  const { theme } = useTheme();
  const positionsRef = useRef<HTMLDivElement>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  
  const allJobs = getAllJobs();
  const departments = getUniqueDepartments();
  const positions = selectedDepartment === 'All' 
    ? allJobs 
    : allJobs.filter(job => job.department === selectedDepartment);

  const handleApplyClick = (job: Job) => {
    setSelectedJob(job);
    setShowApplicationForm(true);
  };

  const scrollToPositions = () => {
    positionsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Background3D />
      {selectedJob && (
        <JobApplicationForm 
          job={selectedJob}
          isOpen={showApplicationForm}
          onClose={() => setShowApplicationForm(false)}
        />
      )}
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="max-w-[1440px] mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h1 className="text-6xl font-normal mb-8 leading-tight">
                  Join us in shaping the future of <span className="text-gradient">AI</span>
                </h1>
                <p className={`text-xl leading-relaxed mb-8 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  We're looking for exceptional individuals who share our passion for advancing AI technology 
                  and making a positive impact on the world. All positions are remote and voluntary, 
                  offering equity participation in groundbreaking AI research.
                </p>
                <div className="flex items-center space-x-6">
                  <button 
                    onClick={scrollToPositions}
                    className="group inline-flex items-center px-6 py-3 text-lg text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-full hover:from-primary-700 hover:to-primary-600 transition-all duration-300 shadow-lg hover:shadow-primary-500/25"
                  >
                    View Remote Opportunities
                    <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-radial from-primary-500/20 to-transparent opacity-70"></div>
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80"
                  alt="Team Collaboration"
                  className="w-full h-[500px] object-cover rounded-3xl shadow-2xl hover-card"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section ref={positionsRef} className={`py-32 ${theme === 'dark' ? 'bg-dark-200/50 backdrop-blur-xl' : 'bg-white/50'}`}>
          <div className="max-w-[1440px] mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-normal mb-4">Remote Volunteer Opportunities</h2>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Join our global remote team and contribute to cutting-edge AI research while earning equity in the future of technology
              </p>
            </div>

            {/* Department Filter */}
            <div className="mb-12">
              <div className="flex items-center justify-center mb-6">
                <Filter className="w-5 h-5 mr-2 text-primary-500" />
                <span className={`text-lg font-medium ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Filter by Department
                </span>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  onClick={() => setSelectedDepartment('All')}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                    selectedDepartment === 'All'
                      ? 'bg-primary-600 text-white shadow-lg'
                      : theme === 'dark'
                      ? 'bg-dark-300 text-gray-300 hover:bg-dark-200 hover:text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Departments ({allJobs.length})
                </button>
                
                {departments.map((department) => {
                  const departmentCount = allJobs.filter(job => job.department === department).length;
                  return (
                    <button
                      key={department}
                      onClick={() => setSelectedDepartment(department)}
                      className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                        selectedDepartment === department
                          ? 'bg-primary-600 text-white shadow-lg'
                          : theme === 'dark'
                          ? 'bg-dark-300 text-gray-300 hover:bg-dark-200 hover:text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {department} ({departmentCount})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Results Counter */}
            <div className="text-center mb-8">
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {selectedDepartment === 'All' 
                  ? `Showing all ${positions.length} volunteer opportunities`
                  : `Showing ${positions.length} opportunities in ${selectedDepartment}`
                }
              </p>
            </div>

            <div className="grid gap-8 transition-all duration-500">
              {positions.length === 0 ? (
                <div className={`text-center py-16 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <p className="text-xl mb-4">No positions found in {selectedDepartment}</p>
                  <p>Try selecting a different department or view all opportunities</p>
                </div>
              ) : (
                positions.map((position, index) => (
                <div
                  key={index}
                  className={`p-8 rounded-2xl hover-card backdrop-blur-lg ${
                    theme === 'dark' 
                      ? 'bg-dark-300/80 hover:bg-dark-300 border border-dark-200' 
                      : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex-1">
                      <h3 className="text-2xl font-normal mb-4">{position.title}</h3>
                      <div className="flex flex-wrap gap-4 mb-4">
                        <span className="inline-flex items-center text-sm">
                          <MapPin className="w-4 h-4 mr-1 text-primary-500" />
                          {position.location}
                        </span>
                        <span className="inline-flex items-center text-sm">
                          <Clock className="w-4 h-4 mr-1 text-primary-500" />
                          {position.type}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          theme === 'dark' ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-100 text-primary-700'
                        }`}>
                          {position.department}
                        </span>
                      </div>
                      <p className={`mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {position.description}
                      </p>
                      <div className="space-y-2">
                        {position.requirements.slice(0, 3).map((req, reqIndex) => (
                          <div
                            key={reqIndex}
                            className={`flex items-center ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}
                          >
                            <div className="w-2 h-2 rounded-full bg-primary-500 mr-3"></div>
                            {req}
                          </div>
                        ))}
                        {position.requirements.length > 3 && (
                          <div className={`text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            +{position.requirements.length - 3} more requirements
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-8 lg:mt-0 lg:ml-8 flex flex-col sm:flex-row lg:flex-col gap-3">
                      <Link
                        to={`/careers/${position.id}`}
                        className="group inline-flex items-center justify-center px-6 py-3 text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-full hover:from-primary-700 hover:to-primary-600 transition-all duration-300 shadow-lg hover:shadow-primary-500/25"
                      >
                        View Details
                        <ArrowUpRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                      </Link>
                      <button
                        onClick={() => handleApplyClick(position)}
                        className={`inline-flex items-center justify-center px-6 py-3 rounded-full font-medium border transition-colors ${
                          theme === 'dark'
                            ? 'border-dark-300 text-gray-300 hover:border-primary-500 hover:text-primary-300'
                            : 'border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-600'
                        }`}
                      >
                        Apply to Volunteer
                      </button>
                    </div>
                  </div>
                </div>
              ))
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}